/**
 * Jobs API Service
 * API client for job-related endpoints (jobs, customers, vehicles, drivers, accident details, documents)
 */

import axios, { AxiosInstance } from 'axios';
import {
  attachAuthRequestInterceptor,
  attachAuthResponseInterceptor,
} from '@/lib/auth/axios-auth';
import type {
  // Job types
  Job,
  JobCreate,
  JobUpdate,
  JobListItem,
  Customer,
  CustomerCreate,
  CustomerUpdate,
  Vehicle,
  VehicleCreate,
  VehicleUpdate,
  Driver,
  DriverCreate,
  DriverUpdate,
  AccidentDetail,
  AccidentDetailCreate,
  AccidentDetailUpdate,
} from '@/types/job';

import type {
  // Document types
  Document,
  DocumentPage,
  DocumentUploadRequest,
  DocumentUploadResponse,
  DocumentClassifyRequest,
  DocumentClassifyResponse,
  DocumentExtractRequest,
  DocumentExtractResponse,
  FieldVerifyRequest,
  BulkFieldVerifyRequest,
  DocumentMapRequest,
  DocumentMapResponse,
  DocumentListItem,
} from '@/types/document';

import type {
  // API types
  PaginatedResponse,
  JobsApiResponse,
  FilterOptions,
  SortOptions,
  ActivityLog,
} from '@/types/common';

// ============================================================================
// API CLIENT CONFIGURATION
// ============================================================================

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

/**
 * Create axios instance with default configuration
 */
function createAxiosInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: `${API_BASE_URL}/api/v1`,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Auth request headers (Authorization, X-Tenant-ID, X-User-Type, etc.)
  attachAuthRequestInterceptor(instance);

  // Data-unwrap + standardized error shape. Note: 401 redirect intentionally
  // omitted here — the refresh-on-401 interceptor below handles token expiry
  // and only redirects when the refresh itself fails.
  instance.interceptors.response.use(
    (response) => response.data,
    (error) => Promise.reject({
      message: error.response?.data?.message || (error instanceof Error ? error.message : String(error)) || 'An error occurred',
      code: error.response?.status,
      details: error.response?.data,
    }),
  );

  // Refresh-on-401 retry. Attached LAST so axios runs it FIRST on the response
  // path (LIFO), allowing the original 401 to be transparently retried with a
  // fresh access token before the data-unwrap interceptor sees it.
  attachAuthResponseInterceptor(instance);

  return instance;
}

const apiClient = createAxiosInstance();

// ============================================================================
// JOBS API
// ============================================================================

export const jobsApi = {
  /**
   * List jobs with pagination and filters
   */
  async list(params?: {
    page?: number;
    page_size?: number;
    filters?: FilterOptions;
    sort?: SortOptions;
  }): Promise<JobsApiResponse<JobListItem>> {
    // Check if user is super admin
    const userType = typeof window !== 'undefined' ? localStorage.getItem('user_type') : null;

    if (userType === 'super_admin') {
      // Super admins use dedicated endpoint to see jobs across all tenants
      return apiClient.get('/super-admin/jobs', { params });
    }

    // Regular users see jobs filtered by their tenant
    return apiClient.get('/jobs', { params });
  },

  /**
   * Get job by ID with optional relations
   */
  async get(id: string, includeRelations = true): Promise<Job> {
    return apiClient.get(`/jobs/${id}`, {
      params: { include_relations: includeRelations },
    });
  },

  /**
   * Create new job
   */
  async create(data: JobCreate): Promise<Job> {
    return apiClient.post('/jobs', data);
  },

  /**
   * Update existing job
   */
  async update(id: string, data: JobUpdate): Promise<Job> {
    return apiClient.patch(`/jobs/${id}`, data);
  },

  /**
   * Delete job
   */
  async delete(id: string): Promise<{ success: boolean }> {
    return apiClient.delete(`/jobs/${id}`);
  },

  /**
   * Get job activity log
   */
  async getActivity(
    id: string,
    params?: { page?: number; page_size?: number }
  ): Promise<PaginatedResponse<ActivityLog>> {
    return apiClient.get(`/jobs/${id}/activity`, { params });
  },

  /**
   * Get job completion status
   */
  async getCompletion(id: string): Promise<{
    overall: number;
    sections: {
      customer: number;
      vehicle: number;
      driver: number;
      accident: number;
      documents: number;
    };
  }> {
    return apiClient.get(`/jobs/${id}/completion`);
  },

  /**
   * Validate job data
   */
  async validate(id: string): Promise<{
    valid: boolean;
    errors: Array<{ section: string; field: string; message: string }>;
    warnings: Array<{ section: string; field: string; message: string }>;
  }> {
    return apiClient.get(`/jobs/${id}/validation`);
  },
};

// ============================================================================
// CUSTOMERS API
// ============================================================================

export const customersApi = {
  /**
   * Get customer by ID
   */
  async get(id: string): Promise<Customer> {
    return apiClient.get(`/customers/${id}`);
  },

  /**
   * Create new customer
   */
  async create(data: CustomerCreate): Promise<Customer> {
    return apiClient.post('/customers', data);
  },

  /**
   * Update existing customer
   */
  async update(id: string, data: CustomerUpdate): Promise<Customer> {
    return apiClient.patch(`/customers/${id}`, data);
  },

  /**
   * Delete customer
   */
  async delete(id: string): Promise<{ success: boolean }> {
    return apiClient.delete(`/customers/${id}`);
  },

  /**
   * Search customers
   */
  async search(
    query: string,
    params?: { page?: number; page_size?: number }
  ): Promise<PaginatedResponse<Customer>> {
    return apiClient.get('/customers/search', {
      params: { query, ...params },
    });
  },
};

// ============================================================================
// VEHICLES API
// ============================================================================

export const vehiclesApi = {
  /**
   * Get vehicle by ID
   */
  async get(id: string): Promise<Vehicle> {
    return apiClient.get(`/vehicles/${id}`);
  },

  /**
   * Create new vehicle
   */
  async create(data: VehicleCreate): Promise<Vehicle> {
    return apiClient.post('/vehicles', data);
  },

  /**
   * Update existing vehicle
   */
  async update(id: string, data: VehicleUpdate): Promise<Vehicle> {
    return apiClient.patch(`/vehicles/${id}`, data);
  },

  /**
   * Delete vehicle
   */
  async delete(id: string): Promise<{ success: boolean }> {
    return apiClient.delete(`/vehicles/${id}`);
  },

  /**
   * Search vehicles by registration number
   */
  async search(registrationNumber: string): Promise<Vehicle[]> {
    return apiClient.get('/vehicles/search', {
      params: { registration_number: registrationNumber },
    });
  },
};

// ============================================================================
// DRIVERS API
// ============================================================================

export const driversApi = {
  /**
   * Get driver by ID
   */
  async get(id: string): Promise<Driver> {
    return apiClient.get(`/drivers/${id}`);
  },

  /**
   * Create new driver
   */
  async create(data: DriverCreate): Promise<Driver> {
    return apiClient.post('/drivers', data);
  },

  /**
   * Update existing driver
   */
  async update(id: string, data: DriverUpdate): Promise<Driver> {
    return apiClient.patch(`/drivers/${id}`, data);
  },

  /**
   * Delete driver
   */
  async delete(id: string): Promise<{ success: boolean }> {
    return apiClient.delete(`/drivers/${id}`);
  },

  /**
   * Search drivers by license number
   */
  async search(licenseNo: string): Promise<Driver[]> {
    return apiClient.get('/drivers/search', {
      params: { license_no: licenseNo },
    });
  },
};

// ============================================================================
// ACCIDENT DETAILS API
// ============================================================================

export const accidentDetailsApi = {
  /**
   * Get accident detail by ID
   */
  async get(id: string): Promise<AccidentDetail> {
    return apiClient.get(`/accident-details/${id}`);
  },

  /**
   * Create new accident detail
   */
  async create(data: AccidentDetailCreate): Promise<AccidentDetail> {
    return apiClient.post('/accident-details', data);
  },

  /**
   * Update existing accident detail
   */
  async update(id: string, data: AccidentDetailUpdate): Promise<AccidentDetail> {
    return apiClient.patch(`/accident-details/${id}`, data);
  },

  /**
   * Delete accident detail
   */
  async delete(id: string): Promise<{ success: boolean }> {
    return apiClient.delete(`/accident-details/${id}`);
  },
};

// ============================================================================
// DOCUMENTS API
// ============================================================================

export const documentsApi = {
  /**
   * List documents for a job
   */
  async list(
    jobId: string,
    params?: {
      page?: number;
      page_size?: number;
      document_type?: string;
      document_category?: string;
      status?: string;
    }
  ): Promise<PaginatedResponse<DocumentListItem>> {
    return apiClient.get(`/jobs/${jobId}/documents`, { params });
  },

  /**
   * Get document by ID
   */
  async get(id: string, includePages = true): Promise<Document> {
    return apiClient.get(`/documents/${id}`, {
      params: { include_pages: includePages },
    });
  },

  /**
   * Upload document (Two-step process)
   * Step 1: Upload to storage-service (MinIO)
   * Step 2: Create document record in job-service
   *
   * NOTE: This method handles the full two-step process internally
   */
  async upload(data: DocumentUploadRequest): Promise<DocumentUploadResponse & { file_url: string; object_path: string }> {
    // Import storage client dynamically to avoid circular dependencies
    const { storageApiClient } = await import('./storage-api');

    if (!data.tenant_id) {
      throw new Error('tenant_id is required for document upload');
    }

    // Step 1: Upload file to storage-service (MinIO with per-tenant bucket)
    const storageResult = await storageApiClient.uploadDocument({
      tenant_id: data.tenant_id,
      job_id: data.job_id,
      file: data.file,
      original_filename: data.file.name,
      ...(data.document_type !== undefined ? { document_type: data.document_type } : {}),
    });

    // Step 2: Create document record in job-service with MinIO path
    const documentResponse = await apiClient.post(`/jobs/${data.job_id}/documents`, {
      tenant_id: data.tenant_id,
      normalized_name: storageResult.normalized_name,
      original_filename: data.file.name,
      file_url: storageResult.file_url,
      file_size: storageResult.file_size,
      mime_type: storageResult.mime_type,
      document_type: data.document_type,
      document_category: data.document_category,
      title: data.title,
      description: data.description,
    });

    return {
      ...documentResponse.data,
      file_url: storageResult.file_url,
      object_path: storageResult.object_path,
    };
  },

  /**
   * Classify document (AI)
   */
  async classify(data: DocumentClassifyRequest): Promise<DocumentClassifyResponse> {
    return apiClient.post(`/documents/${data.document_id}/classify`, data);
  },

  /**
   * Extract data from document (AI + OCR)
   */
  async extract(data: DocumentExtractRequest): Promise<DocumentExtractResponse> {
    return apiClient.post(`/documents/${data.document_id}/extract`, data);
  },

  /**
   * Delete document
   */
  async delete(id: string): Promise<{ success: boolean }> {
    return apiClient.delete(`/documents/${id}`);
  },

  /**
   * Get document page
   */
  async getPage(documentId: string, pageNumber: number): Promise<DocumentPage> {
    return apiClient.get(`/documents/${documentId}/pages/${pageNumber}`);
  },

  /**
   * Verify single extracted field
   */
  async verifyField(pageId: string, data: FieldVerifyRequest): Promise<DocumentPage> {
    return apiClient.patch(`/document-pages/${pageId}/verify`, data);
  },

  /**
   * Verify multiple fields in bulk
   */
  async verifyFieldsBulk(
    pageId: string,
    data: BulkFieldVerifyRequest
  ): Promise<DocumentPage> {
    return apiClient.patch(`/document-pages/${pageId}/verify-bulk`, data);
  },

  /**
   * Map document to entity (customer, vehicle, driver, accident)
   */
  async mapToEntity(documentId: string, data: DocumentMapRequest): Promise<DocumentMapResponse> {
    return apiClient.post(`/documents/${documentId}/map`, data);
  },

  /**
   * Get document mappings
   */
  async getMappings(
    documentId: string
  ): Promise<{
    mappings: Array<{
      entity_type: string;
      entity_id: string;
      field_count: number;
      mapped_at: string;
    }>;
  }> {
    return apiClient.get(`/documents/${documentId}/mappings`);
  },
};

// ============================================================================
// EXPORT ALL
// ============================================================================

/**
 * Unified jobs API client
 */
export const jobsApiClient = {
  jobs: jobsApi,
  customers: customersApi,
  vehicles: vehiclesApi,
  drivers: driversApi,
  accidentDetails: accidentDetailsApi,
  documents: documentsApi,
};

export default jobsApiClient;
